import React, { useMemo } from 'react';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { Map } from 'immutable';

import GenericSmallIconButton from 'flow/components/buttons/GenericSmallIconButton';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import Divider from 'flow/components/ui-components/Divider';
import Tooltip from 'flow/components/ui-components/Tooltip';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { useAppSelector } from 'flow/hooks/useRedux';
import { getContact } from 'flow/selectors/contact-selectors';
import {
    selectOwnExtensionKey,
    selectOwnUserHasCapability,
} from 'flow/selectors/ExtensionSelectors';
import { selectOwnChatUserKey } from 'flow/selectors/own-extension-selectors';
import { selectSessionImages } from 'flow/selectors/chat-message-selectors';
import { selectSessionCloudFiles } from 'flow/selectors/social-integration-selectors';
import { ChatMember, ChatSession } from 'flow/types/Chats';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { getAttachmentLink, getAttachmentPreviewURL } from 'flow/utils';
import { Capabilities } from 'flow/utils/capability-utils';
import { getCallableNumberFromContact } from 'flow/utils/extension-utils';
import { isDirectMessageSession } from 'flow/utils/message-utils-TW';
import { twMerge } from 'tailwind-merge';
import { roomTypes } from './messages/MessageInput';

interface Props {
    session: TypedMap<ChatSession>;
    extensionKey: ExtensionKey;
    shouldShowChatShortcuts: boolean;
}

const MessagesShortcuts = ({
    session,
    extensionKey,
    shouldShowChatShortcuts,
}: Props) => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const { pathname } = useLocation();
    const { hasFeature } = useFeatureFlags();
    const { openModal } = useModal();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const ownChatUserKey = useAppSelector(selectOwnChatUserKey);

    const ownUserHasCapabilityVideo = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.VIDEO),
    );

    const contactKey = session
        ?.get('members')
        ?.find((m) => m.get('extensionKey') !== ownExtensionKey)
        ?.getIn(['contact', 'key']);

    const contact = useAppSelector((state) =>
        getContact(state, contactKey, extensionKey),
    );

    const isAISummarizedEnabled = hasFeature(
        FeatureFlags.AI_SUMMARIZE_UNREAD_MESSAGES,
    );

    const isDirectMessage = isDirectMessageSession(session);

    const shouldShowVideoIcon = ownUserHasCapabilityVideo;

    const getParticipantKey = (
        members: TypedMap<Record<number, TypedMap<ChatMember>>>,
    ) => {
        const index =
            members.getIn([0, 'extensionKey']) === ownExtensionKey ? 1 : 0;

        return members.getIn([index, 'extensionKey']);
    };

    const fileDetails = useAppSelector((state) =>
        state.chats.getIn(['details', session.get('key')], Map()),
    );

    const storedImages = useAppSelector((state) =>
        selectSessionImages(state, session.get('key')),
    );
    const files = useMemo(
        () => storedImages?.reverse()?.slice(0, 3),
        [storedImages],
    );

    const hasCloudFilesIntegration = useAppSelector((state) =>
        state.socialIntegration.getIn(['cloudFiles', 'status', 'active']),
    );

    const storedCloudFiles = useAppSelector((state) =>
        selectSessionCloudFiles(state, session.get('key')),
    );
    const cloudFiles = useMemo(
        () => storedCloudFiles?.reverse()?.slice(0, 3),
        [storedCloudFiles],
    );

    const canAddMember = () =>
        session?.get('roomType') === roomTypes.session ||
        session?.get('admins')?.includes(ownChatUserKey);

    return (
        <aside
            className={twMerge(
                'hidden flex-col gap-3 bg-gray-100 p-4 md:flex',
                !shouldShowChatShortcuts ? '!hidden' : null,
            )}
        >
            <GenericSmallIconButton
                onClick={() =>
                    history.push(
                        generatePath(routes['messages.id.settings'], {
                            id: session.get('key'),
                        }),
                    )
                }
                size="small"
                iconKey="setting_menu"
                label={i18next.t('Chat.Details.GoToDetails')}
                tooltipPlacement="start"
            />

            <Divider className="my-0" />

            {isDirectMessage ? (
                <GenericSmallIconButton
                    size="small"
                    iconKey="person"
                    label={i18next.t('Chat.Details.GoToContactCard')}
                    tooltipPlacement="start"
                    onClick={() =>
                        history.push(
                            generatePath(
                                routes['messages.id.settings.contact.key'],
                                {
                                    extensionKey: getParticipantKey(
                                        session.get('members'),
                                    ),
                                    id: session.get('key'),
                                },
                            ),
                            { from: pathname },
                        )
                    }
                />
            ) : null}
            {session?.get('roomType') === roomTypes.room ||
            session?.get('members')?.size >= 2 ? (
                <GenericSmallIconButton
                    size="small"
                    iconKey="people"
                    label={i18next.t('chatsettings.members')}
                    tooltipPlacement="start"
                    onClick={() =>
                        history.push(
                            generatePath(routes['messages.id.members'], {
                                id: session.get('key'),
                            }),
                            { from: pathname },
                        )
                    }
                />
            ) : null}
            {isAISummarizedEnabled ? (
                <GenericSmallIconButton
                    size="small"
                    iconKey="autoFixWand"
                    label={i18next.t(
                        'ai.chat.summary.button.tooltip.summarize_all',
                    )}
                    tooltipPlacement="start"
                    onClick={() =>
                        openModal('generateChatSummary', {
                            chatSessionKey: session.get('key'),
                        })
                    }
                />
            ) : null}
            {isDirectMessage ? (
                <CallAdaptiveButton
                    phoneNumber={getCallableNumberFromContact(contact)}
                    extensionKey={extensionKey}
                    transferContact={contact}
                    tooltipPlacement="start"
                    bgColor={'bg-gray-200'}
                />
            ) : null}
            {shouldShowVideoIcon && (
                <GenericSmallIconButton
                    size="small"
                    iconKey="video"
                    label={i18next.t('videoconference.general.meeting_room')}
                    tooltipPlacement="start"
                    onClick={() =>
                        history.push(
                            generatePath(routes['messages.id.lobby'], {
                                id: session.get('key'),
                            }),
                        )
                    }
                />
            )}

            {canAddMember() && (
                <GenericSmallIconButton
                    size="small"
                    iconKey="person_add"
                    label={i18next.t('chatsettings.add_member')}
                    tooltipPlacement="start"
                    onClick={() =>
                        openModal('addMessageMember', {
                            id: session.get('key'),
                        })
                    }
                />
            )}

            {hasCloudFilesIntegration && cloudFiles.size > 0 ? (
                <>
                    <Divider className="my-1" />
                    <div className="flex flex-col items-center justify-center gap-3">
                        {cloudFiles.map((file) => (
                            <Tooltip
                                key={file.get('url') || file.get('name')}
                                label={file.get('name')}
                                placement="start"
                            >
                                <a
                                    href={file.get('url')}
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                >
                                    <img
                                        className="size-7 rounded-sm object-cover hover:shadow-md"
                                        src={file.get('iconUrl')}
                                        alt={file.get('name') || ''}
                                    />
                                </a>
                            </Tooltip>
                        ))}

                        <GenericSmallIconButton
                            size="small"
                            className="block"
                            iconKey="more_horiz"
                            label={i18next.t('Chat.Settings.ViewAll')}
                            tooltipPlacement="start"
                            onClick={() =>
                                history.push(
                                    generatePath(routes['messages.id.drive'], {
                                        id: session.get('key'),
                                    }),
                                )
                            }
                        />
                    </div>
                </>
            ) : null}

            {fileDetails?.get('numberOfImages') ? (
                <>
                    <Divider className="my-1" />

                    <div className="flex flex-col gap-2">
                        {files.map((file, i) => {
                            const attachment = file?.get('attachment');

                            if (!attachment?.get('fileName')) {
                                return;
                            }

                            const gifRegExp = new RegExp(/\.gif$/i);
                            const src = gifRegExp.exec(
                                attachment.get('fileName'),
                            )
                                ? getAttachmentLink(
                                      session.get('key'),
                                      attachment,
                                  )
                                : getAttachmentPreviewURL(attachment);

                            return (
                                <button
                                    key={attachment?.get('uuid')}
                                    type="button"
                                    className="flex w-full justify-start border-0 bg-transparent p-0"
                                    onClick={() =>
                                        openModal('imagePreview', {
                                            messageKey: file.get('key'),
                                            sessionKey: session.get('key'),
                                        })
                                    }
                                    aria-label={
                                        attachment?.get('fileName') ||
                                        'Preview image'
                                    }
                                >
                                    <img
                                        src={src}
                                        className="size-8 shrink-0 cursor-pointer rounded-sm object-cover hover:shadow-md"
                                        alt={attachment?.get('fileName') || ''}
                                    />
                                </button>
                            );
                        })}
                    </div>

                    <GenericSmallIconButton
                        size="small"
                        className="block"
                        iconKey="more_horiz"
                        label={i18next.t('chat.shortcut.showAllImages')}
                        tooltipPlacement="start"
                        onClick={() =>
                            history.push(
                                generatePath(routes['messages.id.images'], {
                                    id: session.get('key'),
                                }),
                                { from: pathname },
                            )
                        }
                    />
                </>
            ) : null}
        </aside>
    );
};

export default MessagesShortcuts;
